import React from 'react';
import './theme.css';

/** One one-off stat sample, tagged with the step it came from. */
export interface StepStat {
  stepRole: string;
  metricName: string;
  value: number;
}

interface StepStatsPanelProps {
  /** One-off stats received so far, in arrival order -- e.g. record counts, split sizes, hyperparameters. */
  stats: StepStat[];
}

/**
 * Renders one-off per-step stats (record counts, split sizes,
 * hyperparameters) as a simple grouped list -- distinct from
 * LiveMetricChart's time-series sparkline, since these are single facts
 * per run rather than a value that changes over time. Grouped by
 * `step_role` (not hardcoded to any one step) so a future step's own
 * stats appear here automatically with zero changes to this component --
 * matches metric_point's deliberately-open metric_name design (see
 * SmartWaterProtocol's contract.py module docstring).
 *
 * Renders nothing fabricated while no stats have arrived yet -- an
 * explicit "not yet available" state, per coding-standards.md's
 * over-helpfulness guard, matching EvalScorecard's null-result convention.
 *
 * Dedupes by (step_role, metric_name), keeping the latest value -- a
 * retried step (both PreProcess and Train have real retry_policies
 * configured in nfl_training_pipeline.py) re-emits the same stat names on
 * each attempt, and rendering every emission would produce duplicate
 * React keys as well as stale/confusing repeated stats.
 */
export function StepStatsPanel({ stats }: StepStatsPanelProps) {
  if (stats.length === 0) {
    return (
      <div className="scorecard">
        <div className="title">No step stats received yet.</div>
      </div>
    );
  }

  const latestByStepAndMetric = new Map<string, StepStat>();
  for (const stat of stats) {
    latestByStepAndMetric.set(`${stat.stepRole}::${stat.metricName}`, stat);
  }

  const statsByStep = new Map<string, StepStat[]>();
  for (const stat of latestByStepAndMetric.values()) {
    const forStep = statsByStep.get(stat.stepRole) ?? [];
    forStep.push(stat);
    statsByStep.set(stat.stepRole, forStep);
  }

  return (
    <div className="scorecard">
      <div className="title">Step stats</div>
      {[...statsByStep.entries()].map(([stepRole, stepStats]) => (
        <div key={stepRole} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <div className="ring-label" style={{ textTransform: 'uppercase' }}>
            {stepRole}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, fontSize: 12, color: 'var(--text-dim)' }}>
            {stepStats.map((stat) => (
              <span key={stat.metricName}>
                {stat.metricName}: <b style={{ color: 'var(--text)' }}>{stat.value}</b>
              </span>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
